"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import PeachIcon from "@/components/icons/PeachIcon";
import { Mail } from "lucide-react";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

export default function SignupPage() {
  const router = useRouter();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  async function handleGoogleSignIn() {
    setGoogleLoading(true);
    const supabase = createClient();
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
  }

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (username.length < 3 || username.length > 20) {
      setError("Username must be 3–20 characters.");
      setLoading(false);
      return;
    }

    const supabase = createClient();

    // Pre-check: username taken?
    const { data: existing } = await supabase
      .from("profiles")
      .select("id")
      .eq("username", username)
      .maybeSingle();
    if (existing) {
      setError("That username is already taken. Please choose a different one.");
      setLoading(false);
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { username } },
    });

    if (error) {
      // Map opaque DB trigger errors to a readable message
      const msg = error.message.toLowerCase();
      if (msg.includes("database error") || msg.includes("saving new user")) {
        setError("That username or email is already registered. Try signing in instead.");
      } else if (msg.includes("already registered") || msg.includes("user already exists")) {
        setError("An account with this email already exists. Try signing in.");
      } else {
        setError(error.message);
      }
      setLoading(false);
      return;
    }

    // If email confirmation is enabled, session will be null after signUp
    if (!data.session) {
      setConfirmed(true);
      setLoading(false);
      return;
    }

    // Auto-confirmed (email confirmation disabled) — go straight to onboarding
    router.push("/workflows/new?onboarding=1");
    router.refresh();
  }

  if (confirmed) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4" style={{ background: "linear-gradient(135deg, #fff8f5 0%, #ffe9de 100%)" }}>
        <div className="w-full max-w-sm text-center">
          <div className="flex justify-center mb-5">
            <div className="w-16 h-16 rounded-full bg-peach-100 flex items-center justify-center">
              <Mail size={28} color="#ff6b35" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-peach-900 mb-2">Check your inbox</h1>
          <p className="text-peach-500 text-sm leading-relaxed mb-6">
            {`We sent a confirmation link to ${email}. Click it to activate your account and get started.`}
          </p>
          <p className="text-xs text-peach-400">
            Didn&apos;t get it? Check your spam folder or{" "}
            <button
              onClick={() => setConfirmed(false)}
              className="underline hover:text-peach-600 transition-colors"
            >
              try again
            </button>
            .
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-4" style={{ background: "linear-gradient(135deg, #fff8f5 0%, #ffe9de 100%)" }}>
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center"><PeachIcon size={56} /></div>
          <h1 className="text-2xl font-bold mt-3" style={{ color: "#9e3210" }}>Join PeachLog</h1>
          <p className="text-sm mt-1" style={{ color: "#ff8c5a" }}>Track your creative progress, day by day.</p>
        </div>

        <div className="card p-6 space-y-4">
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-full border border-gray-200 bg-white shadow-sm hover:bg-gray-50 transition-colors font-medium text-sm text-gray-700 disabled:opacity-60"
          >
            <GoogleIcon />
            {googleLoading ? "Redirecting…" : "Continue with Google"}
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-peach-100" />
            <span className="text-xs text-peach-300">or</span>
            <div className="flex-1 h-px bg-peach-100" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label" htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                className="input"
                placeholder="yourname"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                autoComplete="username"
                required
                minLength={3}
                maxLength={20}
              />
              <p className="text-xs mt-1 text-peach-400">3–20 characters. Letters, numbers, and underscores only.</p>
            </div>
            <div>
              <label className="label" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className="input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                className="input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>

            {error && (
              <p className="text-sm rounded-xl px-3 py-2" style={{ color: "#dc2626", background: "#fef2f2" }}>
                {error}
              </p>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? "Creating account…" : "Create account"}
            </button>
          </form>
        </div>

        <p className="text-center text-sm mt-4" style={{ color: "#ff8c5a" }}>
          Already have an account?{" "}
          <Link href="/auth/login" className="font-medium hover:underline" style={{ color: "#e8521a" }}>
            Sign in
          </Link>
        </p>
        <p className="text-center mt-4" style={{ fontSize: 11, color: "#ffaf85" }}>
          By signing up you agree to our{" "}
          <Link href="/terms" className="underline hover:text-peach-600">Terms</Link>
          {" "}and{" "}
          <Link href="/privacy" className="underline hover:text-peach-600">Privacy Policy</Link>
        </p>
      </div>
    </main>
  );
}
